import React, { useMemo, useCallback, useState } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, RotateCcw } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import useLayoutBucket from '../../hooks/useLayoutBucket';
import { drawerGeometry } from '../../components/layout/shellMetrics';
import { formatDateTime } from '../../utils/Utils';
import { Badge } from './config';
import {
  useWebhookDeliveries, eventLabel, DELIVERY_STATUSES, DELIVERY_STATUS_COLORS,
} from './config/webhooks';

// Uç başına teslimat günlüğü — SALT OKUNUR panel (tasarım belgesi §6.5): durum, deneme
// sayısı, son hata, yanıt kodu, sonraki deneme zamanı; kalıcı başarısız (DLQ) satırda
// "Yeniden Gönder". Neden GenericFormDrawer DEĞİL: bu bir form değil (OfferApprovalChainDrawer /
// BulkJobsDrawer ile aynı gerekçe); kabuk ölçüleri onlarla aynıdır (FE-08, drawerGeometry).
//
// TEK KAYDIRMA: çekmece gövdesi kaymaz (overflow: hidden) — kaydırma yalnız tablonun
// satırlarındadır. Sayfalama ve durum süzgeci SUNUCU tarafında (teslimat geçmişi büyüyebilir).

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
  flex-shrink: 0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Muted = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #B3261E;
  word-break: break-word;
`;

const STATUS_COLUMN = 'status';

export default function WebhookDeliveriesDrawer({
  endpoint, catalog, canReplay, onClose,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const geometry = drawerGeometry(useLayoutBucket());
  const open = Boolean(endpoint);

  const {
    fetching, failed, rows, total, query, setQuery, replay,
  } = useWebhookDeliveries(endpoint?.id || null);
  const [replayingId, setReplayingId] = useState(null);

  const labels = useMemo(
    () => new Map((catalog || []).map((e) => [e.eventKey, e.label])),
    [catalog],
  );

  const handleReplay = useCallback(async (row) => {
    setReplayingId(row.id);
    await replay(row.id);
    setReplayingId(null);
  }, [replay]);

  const columns = useMemo(() => [
    {
      name: 'eventKey',
      label: t('webhooks.event'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => eventLabel(t, v, labels.get(v)),
      },
    },
    {
      name: STATUS_COLUMN,
      label: t('webhooks.status'),
      options: {
        sort: false,
        filter: true,
        filterType: 'dropdown',
        filterOptions: {
          names: DELIVERY_STATUSES,
          renderValue: (v) => t(`webhooks.deliveryStatus.${v}`),
        },
        customFilterListOptions: { render: (v) => t(`webhooks.deliveryStatus.${v}`) },
        customBodyRender: (v) => {
          const cfg = DELIVERY_STATUS_COLORS[v] || DELIVERY_STATUS_COLORS.pending;
          return (
            <Badge data-role="delivery-status" data-status={v} $color={cfg.color} $bg={cfg.bg}>
              {t(`webhooks.deliveryStatus.${v}`)}
            </Badge>
          );
        },
      },
    },
    {
      name: 'attemptCount',
      label: t('webhooks.attempts'),
      options: { filter: false, sort: false },
    },
    {
      name: 'responseStatus',
      label: t('webhooks.result'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <Stack>
              {v ? <span data-role="delivery-response">{`HTTP ${v}`}</span> : null}
              {row.lastError ? <ErrorText data-role="delivery-error">{row.lastError}</ErrorText> : null}
              {!v && !row.lastError ? <Muted>-</Muted> : null}
            </Stack>
          );
        },
      },
    },
    {
      name: 'enqueuedAt',
      label: t('webhooks.time'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          return (
            <Stack>
              <span>{formatDateTime(v, lang)}</span>
              {row?.nextAttemptAt && (
                <Muted data-role="delivery-next-attempt">
                  {t('webhooks.nextAttempt')}
                  {': '}
                  {formatDateTime(row.nextAttemptAt, lang)}
                </Muted>
              )}
            </Stack>
          );
        },
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          // Yeniden gönderme YALNIZ PA.Write'ta ve YALNIZ kalıcı başarısız satırda.
          if (!row || !canReplay || !row.canReplay) return null;
          return (
            <Tooltip text={t('webhooks.replay')}>
              <IconButton
                type="button"
                size="sm"
                data-role={`delivery-replay-${row.id}`}
                aria-label={t('webhooks.replay')}
                disabled={replayingId === row.id}
                onClick={() => handleReplay(row)}
              >
                <RotateCcw size={16} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
  ], [t, lang, labels, rows, canReplay, replayingId, handleReplay]);

  const handleTableChanged = useCallback((action, tableState) => {
    const statusIndex = columns.findIndex((c) => c.name === STATUS_COLUMN);
    const status = tableState?.filterList?.[statusIndex]?.[0] || null;
    setQuery({
      page: (tableState?.page || 0) + 1,
      pageSize: tableState?.rowsPerPage || query.pageSize,
      status,
    });
  }, [columns, setQuery, query.pageSize]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={geometry.size}
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: geometry.minWidth,
        margin: geometry.margin,
        borderRadius: geometry.borderRadius,
        height: geometry.height,
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title title={endpoint?.name}>{endpoint?.name}</Title>
        <CloseBtn onClick={onClose} aria-label={t('common.close')}><X size={20} /></CloseBtn>
      </Header>
      <Body data-role="webhook-deliveries">
        {failed && <LoadErrorBanner message={t('webhooks.deliveriesLoadFailed')} />}
        {open && (
          <DataTable
            title={t('webhooks.deliveries')}
            fetching={fetching}
            columns={columns}
            data={rows}
            serverSide
            totalRowCount={total}
            pageIndex={query.page - 1}
            pageSize={query.pageSize}
            onTableChanged={handleTableChanged}
            tableBodyMaxHeight="calc(100vh - 260px)"
          />
        )}
      </Body>
    </Drawer>
  );
}
